import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import type { RechnungsStatus, RechnungsTyp } from '../lib/api-enums';
import {
  RECHNUNGS_TYP_LABELS as TYP_LABELS,
  RECHNUNGS_STATUS_LABELS as STATUS_LABELS,
  RECHNUNGS_STATUS_BADGE as STATUS_COLORS,
} from '../lib/api-labels';

interface Rechnung {
  id: string;
  rechnungsnummer: string | null;
  typ: RechnungsTyp;
  status: RechnungsStatus;
  kundeId: string;
  titel: string;
  rechnungsdatum: string | null;
  zahlungszielTage: number;
  summeBrutto: string;
  bezahltAm: string | null;
  versendetAm: string | null;
  createdAt: string;
}

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

export const Route = createFileRoute('/rechnungen/')({
  beforeLoad: () => requireAccess('/api/v1/rechnungen'),
  component: RechnungenPage,
});

function RechnungenPage() {
  const [typFilter, setTypFilter] = useState<'' | RechnungsTyp>('');
  const [statusFilter, setStatusFilter] = useState<'' | RechnungsStatus>('');
  const [kundeFilter, setKundeFilter] = useState('');

  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const listQ = useQuery({
    queryKey: ['rechnungen', typFilter, statusFilter, kundeFilter],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (typFilter) qs.set('typ', typFilter);
      if (statusFilter) qs.set('status', statusFilter);
      if (kundeFilter) qs.set('kundeId', kundeFilter);
      const path = `/api/v1/rechnungen${qs.toString() ? '?' + qs.toString() : ''}`;
      return api<{ items: Rechnung[] }>(path);
    },
  });

  const kundeMap = new Map((kundenQ.data?.items ?? []).map((k) => [k.id, k]));

  return (
    <div className="space-y-4">
      <PageHeader
        title="Rechnungen"
        description="Ausgangsrechnungen, Abschläge, Schluss, Gutschriften und Storno. Nach Versand sind Kopf und Positionen unveränderbar — DB-Trigger erzwingen das."
        actions={
          <Link
            to="/rechnungen/neu"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium bg-brand-600 hover:bg-brand-700 text-white transition-colors"
          >
            + Neue Rechnung
          </Link>
        }
      />

      <div className="flex items-center gap-3 text-sm flex-wrap">
        <label>
          <span className="text-slate-600 mr-2">Typ</span>
          <Select
            value={typFilter}
            onChange={(e) => setTypFilter(e.target.value as RechnungsTyp | '')}
            className="w-40 inline-block"
          >
            <option value="">alle</option>
            {(Object.keys(TYP_LABELS) as RechnungsTyp[]).map((t) => (
              <option key={t} value={t}>
                {TYP_LABELS[t]}
              </option>
            ))}
          </Select>
        </label>
        <label>
          <span className="text-slate-600 mr-2">Status</span>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as RechnungsStatus | '')}
            className="w-40 inline-block"
          >
            <option value="">alle</option>
            {(Object.keys(STATUS_LABELS) as RechnungsStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        </label>
        <label>
          <span className="text-slate-600 mr-2">Kunde</span>
          <Select
            value={kundeFilter}
            onChange={(e) => setKundeFilter(e.target.value)}
            className="w-72 inline-block"
          >
            <option value="">alle</option>
            {(kundenQ.data?.items ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.kundennummer} — {k.firmenname}
              </option>
            ))}
          </Select>
        </label>
      </div>

      <ErrorBanner error={listQ.error} />

      {listQ.data && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left w-36">Nummer</th>
                <th className="px-4 py-2 text-left w-24">Typ</th>
                <th className="px-4 py-2 text-left">Titel</th>
                <th className="px-4 py-2 text-left">Kunde</th>
                <th className="px-4 py-2 text-center w-32">Status</th>
                <th className="px-4 py-2 text-right w-32">Brutto</th>
                <th className="px-4 py-2 text-left w-28">Datum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {listQ.data.items.map((r) => {
                const k = kundeMap.get(r.kundeId);
                return (
                  <tr key={r.id} className="text-sm text-slate-800">
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">
                      {r.rechnungsnummer ?? <span className="text-slate-400 italic">Entwurf</span>}
                    </td>
                    <td className="px-4 py-2 text-slate-600">{TYP_LABELS[r.typ]}</td>
                    <td className="px-4 py-2 font-medium">
                      <Link
                        to="/rechnungen/$id"
                        params={{ id: r.id }}
                        className="text-brand-700 hover:underline"
                      >
                        {r.titel}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {k ? `${k.kundennummer} — ${k.firmenname}` : r.kundeId.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span
                        className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[r.status]}`}
                      >
                        {STATUS_LABELS[r.status]}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {Number(r.summeBrutto).toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      €
                    </td>
                    <td className="px-4 py-2 text-slate-600 text-xs">{r.rechnungsdatum ?? '–'}</td>
                  </tr>
                );
              })}
              {listQ.data.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    Keine Rechnungen zu diesem Filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
